import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, storageApiRef } from '@backstage/core-plugin-api';
import { Progress } from '@backstage/core-components';
import {
  STORAGE_BUCKET,
  LANDING_PAGE_KEY,
  DEFAULT_LANDING_PAGE,
  STORAGE_LOAD_TIMEOUT_MS,
  getValidLandingPage,
} from './sidebarConstants';

/**
 * Component that redirects from "/" to the user's preferred landing page.
 * Used as the element for the root route to ensure users always go to their
 * configured landing page (or /dashboard by default).
 */
export const RootRedirect = () => {
  const navigate = useNavigate();
  const baseStorageApi = useApi(storageApiRef);
  const storageApi = useMemo(
    () => baseStorageApi.forBucket(STORAGE_BUCKET),
    [baseStorageApi],
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let hasRedirected = false;
    // Declare subscription variable before subscribe to avoid TDZ issues
    // if the observable emits synchronously
    let subscription: { unsubscribe: () => void } | null = null;

    // Helper to safely redirect using shared validation
    const safeRedirect = (landingPage: string | undefined) => {
      const targetPath = getValidLandingPage(landingPage);
      navigate(targetPath, { replace: true });
    };

    // Get the landing page synchronously first (from cache if available)
    const cachedSnapshot = storageApi.snapshot<string>(LANDING_PAGE_KEY);
    if (cachedSnapshot.presence === 'present') {
      safeRedirect(cachedSnapshot.value);
      hasRedirected = true;
      setIsLoading(false);
      // No cleanup needed for synchronous redirect
      return undefined;
    }

    // Subscribe to storage to get the landing page preference (async)
    subscription = storageApi.observe$<string>(LANDING_PAGE_KEY).subscribe({
      next: snapshot => {
        if (snapshot.presence === 'unknown') {
          // Still loading from backend, wait for the next update
          return;
        }

        if (hasRedirected) {
          subscription?.unsubscribe();
          return;
        }

        hasRedirected = true;
        safeRedirect(snapshot.value);
        setIsLoading(false);
        subscription?.unsubscribe();
      },
      error: () => {
        // Handle subscription errors gracefully - redirect to default
        if (!hasRedirected) {
          hasRedirected = true;
          navigate(DEFAULT_LANDING_PAGE, { replace: true });
          setIsLoading(false);
        }
      },
    });

    // Timeout fallback - redirect to default if storage fails to load
    const timeout = setTimeout(() => {
      if (!hasRedirected) {
        hasRedirected = true;
        navigate(DEFAULT_LANDING_PAGE, { replace: true });
        setIsLoading(false);
        subscription?.unsubscribe();
      }
    }, STORAGE_LOAD_TIMEOUT_MS);

    return () => {
      clearTimeout(timeout);
      subscription?.unsubscribe();
    };
  }, [storageApi, navigate]);

  if (isLoading) {
    return <Progress />;
  }

  return null;
};

export default RootRedirect;
